import { deleteDoc, getDoc, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where, } from "firebase/firestore";
import { asTripId, err, newId, ok, permissionDenied, } from "@pack-it-up/shared";
import { toDomainTrip, toTripDto } from "../mappers/index.js";
import { readListContents } from "./listContents.js";
import { logSnapshotError, observeDoc, patchField, toDomainError } from "./support.js";
export const makeTripRepo = (db, p, session) => ({
    observeTrip: (tripId) => observeDoc(p.trip(tripId), toDomainTrip, "trip"),
    observeMyTrips: (userId, opts) => ({
        subscribe(next) {
            const q = query(p.trips(), where("memberUserIds", "array-contains", userId), where("archived", "==", opts.archived));
            const unsub = onSnapshot(q, (snap) => {
                const out = [];
                for (const d of snap.docs) {
                    const r = toDomainTrip(d.data());
                    if (!r.ok)
                        continue;
                    const t = r.value;
                    const m = t.members[userId];
                    const role = m && m.kind === "user" ? m.role : "viewer";
                    // Counts/progress are denormalised onto the trip doc by a function later;
                    // until then a list row shows zeroes rather than fanning out reads here.
                    out.push({
                        id: t.id,
                        name: t.name,
                        ...(t.startDate !== undefined ? { startDate: t.startDate } : {}),
                        ...(t.endDate !== undefined ? { endDate: t.endDate } : {}),
                        ...(t.color !== undefined ? { color: t.color } : {}),
                        role,
                        archived: t.archived,
                        itemCount: 0,
                        todoCount: 0,
                        packingProgress: 0,
                        todoProgress: 0,
                        ...(t.location !== undefined ? { location: t.location } : {}),
                        people: Object.values(t.members).map((mem) => ({ displayName: mem.profile.displayName })),
                    });
                }
                next(out);
            }, logSnapshotError("myTrips"));
            return unsub;
        },
    }),
    async createTrip(input) {
        const u = session.user();
        if (!u)
            return err(permissionDenied("create a trip"));
        const id = newId();
        // T27 — seed the owner's own staging opt-in from their personal default
        // (users/{uid}.defaultStagingOptIn), so new trips start where their last one left off.
        // Best-effort: this is a nice-to-have default, not something that should block trip
        // creation if the read fails for any reason (a prior unguarded throw here left the
        // caller's promise permanently unresolved — see NewTripModal's stuck "Creating…").
        let defaultStagingOptIn = false;
        try {
            const profileSnap = await getDoc(p.user(u.id));
            defaultStagingOptIn = profileSnap.data()?.defaultStagingOptIn ?? false;
        }
        catch {
            // fall through with the default
        }
        const trip = {
            id: asTripId(id),
            name: input.name,
            ...(input.startDate !== undefined ? { startDate: input.startDate } : {}),
            ...(input.endDate !== undefined ? { endDate: input.endDate } : {}),
            ...(input.location !== undefined ? { location: input.location } : {}),
            ...(input.description !== undefined ? { description: input.description } : {}),
            members: {
                // Issue #8 — a copy brings the source trip's members along so its `specific`
                // assignments still name someone. The caller's own entry is written last and
                // wins: whoever creates the trip owns it, whatever role they held on the source.
                ...(input.members ?? {}),
                [u.id]: {
                    kind: "user",
                    role: "owner",
                    profile: { displayName: u.displayName, email: u.email },
                    settings: { stagingOptIn: defaultStagingOptIn },
                },
            },
            visibility: "private",
            settings: { allowProxyCompletion: true },
            importedTemplateIds: [],
            archived: false,
            createdAt: new Date(),
        };
        try {
            await setDoc(p.trip(id), { ...toTripDto(trip), createdAt: serverTimestamp() });
            return ok(asTripId(id));
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    // Issue #8 — the mirror of `TemplateRepository.fetchTemplateRows`: copying a trip
    // is reachable from the trip list, which streams summaries only.
    fetchTripContents: (tripId) => readListContents(p, tripId),
    // One-shot read of the trip doc — the copy needs `members`, which a summary drops.
    async fetchTrip(tripId) {
        try {
            const snap = await getDoc(p.trip(tripId));
            if (!snap.exists())
                return ok(null);
            return toDomainTrip(snap.data());
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    async updateTrip(patch) {
        const out = {};
        patchField(out, "name", patch.name);
        patchField(out, "startDate", patch.startDate);
        patchField(out, "endDate", patch.endDate);
        patchField(out, "location", patch.location);
        patchField(out, "description", patch.description);
        patchField(out, "color", patch.color);
        patchField(out, "visibility", patch.visibility);
        patchField(out, "settings.allowProxyCompletion", patch.settings?.allowProxyCompletion);
        try {
            await updateDoc(p.trip(patch.tripId), out);
            return ok(undefined);
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    // T27 — self-scoped: callers must pass their own userId (enforced by security rules
    // at the `members.<uid>.settings` path).
    async setMemberSettings(tripId, userId, settings) {
        try {
            await updateDoc(p.trip(tripId), { [`members.${userId}.settings`]: settings });
            return ok(undefined);
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    async archiveTrip(tripId, archived) {
        try {
            await updateDoc(p.trip(tripId), { archived });
            return ok(undefined);
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    async deleteTrip(tripId) {
        try {
            await deleteDoc(p.trip(tripId));
            return ok(undefined);
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
});
//# sourceMappingURL=trip.repo.js.map